/**
 * Shopify Add to Cart Animation
 * Instant Feedback with Smart Duplicate Prevention
 */

class CartAnimation {
  constructor() {
    this.cartIcon = null;
    this.activeAnimations = 0;
    this.lastTrigger = new WeakMap();
    this.init();
  }

  init() {
    this.findCartIcon();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bindEventDelegation());
    } else {
      this.bindEventDelegation();
    }

    if (Shopify?.designMode) {
      document.addEventListener('shopify:section:load', () => this.findCartIcon());
    }

    window.cartAnimationInstance = this;
  }

  bindEventDelegation() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest('button[name="add"], .product-form__submit, .quick-add__submit');
      if (button) {
        this.handleClick(e, button);
      }
    });
  }

  findCartIcon() {
    const selectors = ['.header__icon--cart', '#cart-icon-bubble', '.cart-count-bubble', '[href*="/cart"]', '.cart-link'];
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        this.cartIcon = element.closest('a') || element;
        return;
      }
    }
  }

  handleClick(event, button) {
    if (button.closest('.quick_view_btn, modal-opener')) return;
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const form = button.closest('form');
    if (!form?.action?.includes('cart/add')) return;

    const image = this.getImage(button);
    this.triggerAnimation(button, image);
  }

  startAnimation(button, imageSrc) {
    if (!button) return;

    const lastTime = this.lastTrigger.get(button) || 0;
    const now = Date.now();

    if (now - lastTime < 2000) return;

    requestAnimationFrame(() => {
      this.triggerAnimation(button, imageSrc);
    });
  }

  triggerAnimation(button, imageSrc) {
    this.lastTrigger.set(button, Date.now());
    const image = imageSrc || this.getImage(button);
    this.animate(button, image);
  }

  getImage(button) {
    // 1. Button'un parent container'ını bul
    let container = button.closest('.product, .product-single, .product-main');
    
    // Detay sayfası değilse card/grid item'ı bul
    if (!container) {
      container = button.closest('.card, .card-wrapper, .grid__item, article, .product-item');
    }
    
    if (!container) return null;

    // 2. Container içindeki görünür resimleri bul ve en büyüğünü al
    const allImages = Array.from(container.querySelectorAll('img'));
    let bestImage = null;
    let maxSize = 0;

    for (const img of allImages) {
      const src = img.currentSrc || img.src;
      
      // Geçersiz resimleri atla
      if (!src || 
          src.includes('data:image') ||
          src.includes('placeholder') || 
          src.includes('icon') ||
          src.includes('logo') ||
          img.width < 50 || 
          img.height < 50) {
        continue;
      }

      // Görünür mü kontrol et
      const rect = img.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;

      // En büyük resmi bul (ana ürün resmi genelde en büyüktür)
      const size = rect.width * rect.height;
      if (size > maxSize) {
        maxSize = size;
        bestImage = src;
      }
    }

    return bestImage ? this.optimizeUrl(bestImage) : null;
  }

  optimizeUrl(src) {
    if (!src) return null;
    return src.includes('cdn.shopify.com') 
      ? src.split('?')[0] + '?width=100' 
      : src;
  }

  animate(button, imageSrc) {
    if (!this.cartIcon) return;

    this.activeAnimations++;

    const btnRect = button.getBoundingClientRect();
    const cartRect = this.cartIcon.getBoundingClientRect();
    const element = this.createElement(imageSrc);

    const startX = btnRect.left + btnRect.width / 2;
    const startY = btnRect.top + btnRect.height / 2;

    element.style.left = startX + 'px';
    element.style.top = startY + 'px';

    document.body.appendChild(element);
    this.sparkles(startX, startY);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        element.style.left = cartRect.left + cartRect.width / 2 + 'px';
        element.style.top = cartRect.top + cartRect.height / 2 + 'px';
        element.style.transform = 'translate(-50%, -50%) scale(0.3)';
      });
    });

    setTimeout(() => {
      element.remove();
      this.activeAnimations--;

      if (this.activeAnimations === 0) {
        this.bounce();
      }
    }, 1050);
  }

  createElement(src) {
    const el = document.createElement('div');
    el.className = 'cart-anim-element';

    if (src) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.loading = 'eager'; // Hemen yükle
      
      // Yüklenmemişse sparkle göster
      if (!img.complete) {
        el.innerHTML = '<span class="cart-anim-icon">✨</span>';
        img.onload = () => {
          el.innerHTML = '';
          el.appendChild(img);
        };
      } else {
        el.appendChild(img);
      }
      
      img.onerror = () => el.innerHTML = '<span class="cart-anim-icon">✨</span>';
    } else {
      el.innerHTML = '<span class="cart-anim-icon">✨</span>';
    }

    return el;
  }

  sparkles(x, y) {
    const frag = document.createDocumentFragment();

    for (let i = 0; i < 8; i++) {
      const sparkle = document.createElement('div');
      sparkle.className = 'cart-anim-sparkle';
      sparkle.textContent = '✨';

      const angle = (360 / 8) * i;
      const radius = 30 + Math.random() * 20;
      sparkle.style.left = x + Math.cos(angle * Math.PI / 180) * radius + 'px';
      sparkle.style.top = y + Math.sin(angle * Math.PI / 180) * radius + 'px';
      sparkle.style.animationDelay = `${i * 0.05}s`;

      frag.appendChild(sparkle);
      setTimeout(() => sparkle.remove(), 1000);
    }

    document.body.appendChild(frag);
  }

  bounce() {
    if (!this.cartIcon) return;
    this.cartIcon.classList.add('cart-icon-bounce');
    setTimeout(() => this.cartIcon.classList.remove('cart-icon-bounce'), 500);
  }
}

// CSS
const style = document.createElement('style');
style.textContent = `
  .cart-anim-element {
    position: fixed;
    z-index: 10000;
    pointer-events: none;
    width: 50px;
    height: 50px;
    transform: translate(-50%, -50%);
    transition: all 1s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .cart-anim-element img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    border: 2px solid rgba(255, 215, 0, 0.8);
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.8), 0 4px 12px rgba(0,0,0,0.15);
  }

  .cart-anim-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: 32px;
    filter: drop-shadow(0 0 8px rgba(255, 215, 0, 0.6));
  }

  .cart-anim-sparkle {
    position: fixed;
    z-index: 9999;
    pointer-events: none;
    font-size: 20px;
    transform: translate(-50%, -50%) scale(0);
    animation: sparkle-burst 1s ease-out forwards;
  }

  @keyframes sparkle-burst {
    0% { transform: translate(-50%, -50%) scale(0) rotate(0deg); opacity: 1; }
    50% { transform: translate(-50%, -50%) scale(1.2) rotate(180deg); opacity: 1; }
    100% { transform: translate(-50%, -50%) scale(0) rotate(360deg); opacity: 0; }
  }

  .cart-icon-bounce {
    animation: cart-bounce 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @keyframes cart-bounce {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.15); }
  }

  @media (prefers-reduced-motion: reduce) {
    .cart-anim-element, .cart-anim-sparkle, .cart-icon-bounce {
      animation: none !important;
      transition: none !important;
    }
  }
`;

document.head.appendChild(style);
new CartAnimation();
